import { Component, OnInit } from '@angular/core';
import { AuthService } from '../core/authentication/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Shell } from '../shell/shell.service';
import { NavStateService } from '../shell/nav-state.service';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ConfigService } from '../shared/config.service';
import { NgxSpinnerService } from "ngx-spinner";

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss']
})
export class AuthCallbackComponent implements OnInit {

  error: boolean = false;
  loading: boolean = true;
  errorMessage: string | null = null;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private shellService: Shell,
    private navStateService: NavStateService,
    private configService: ConfigService,
    private spinner: NgxSpinnerService
  ) { }

  async ngOnInit() {
    // Mostrar spinner de carga mientras se procesa la autenticación.
    this.spinner.show();

    // Detectar error OIDC en la URL (ej: error=access_denied)
    const params = this.route.snapshot.queryParams;
    if (params['error']) {
      this.error = true;
      this.loading = false;
      const err = params['error'];
      if (err === 'access_denied') {
        this.errorMessage = 'Has cancelado el proceso de autenticación.';
      } else {
        this.errorMessage = 'Error de autenticación: ' + err;
      }
      // Limpia sesión por seguridad
      try {
        await this.authService.clearSessionOnExpiry();
      } catch { }
      // Redirige automáticamente al login con el error después de 2 segundos
      try { this.authService.clearLoginProgress(); } catch { }
      return;
    }

    try {
      await this.authService.completeAuthentication();
      this.error = false;
      this.loading = false;
    } catch (error) {
      this.error = true;
      this.loading = true;
      this.errorMessage = 'Error al completar autenticación.';
      this.router.navigateByUrl(this.configService.spaURI, { replaceUrl: true });

      // Limpia datos OIDC para evitar estados pegados, pero sin romper el estado global
      try {
        await this.authService.clearSessionOnExpiry();
      } catch { }
      // Redirige automáticamente al login con error después de 2 segundos
      try { this.authService.clearLoginProgress(); } catch { }
      return;
    }

    // Verificar si hay caché válido
    const hasCachedMenu = this.navStateService.currentNavbar.length > 0;
    const cacheAge = this.navStateService.getCacheAge();
    const cacheIsValid = cacheAge < 10 * 60 * 1000; // 10 minutos

    // Determine target after login: prefer stored original protected URL, else
    // if cached menu valid navigate to safe root, otherwise preload menu and
    // navigate to safe root. We use '/partner' as a predictable default instead
    // of empty root which sometimes yields a blank page.
    const redirectedUrl = this.authService.getRedirectedFromProtectedUrl();
    const defaultTarget = redirectedUrl || '/home';

    if (hasCachedMenu && cacheIsValid) {
      // clear redirect flag (we will navigate to the resolved target)
      try { this.authService.setRedirectedFromProtectedRoute(false); } catch { }
      this.router.navigateByUrl(defaultTarget, { replaceUrl: true });
      this.spinner.hide();
    } else {
      // Pre-cargar el menú solo si no hay caché válido
      this.shellService.getmenu(this.authService.authorizationHeaderValue)
        .pipe(finalize(() => {this.spinner.hide();}),
          catchError(err => {
            console.error('Error al precargar menú:', err);
            return of([]);
          })
        )
        .subscribe((menu: any) => {
          if (menu && menu.length > 0) {
            this.navStateService.setNavbar(menu);
          }
          // clear redirect flag before navigating
          try { this.authService.setRedirectedFromProtectedRoute(false); } catch { }
          try { this.authService.clearLoginProgress(); } catch { }
          this.router.navigateByUrl(defaultTarget, { replaceUrl: true });
        });
    }
  }
}
